/**
 * Sessão de mentira, para navegar o app sem credencial e sem tocar em produção.
 *
 * ── Por que ela existe ────────────────────────────────────────────────────
 *
 * Passar do login exige credencial do Google, que eu não insiro. Resultado:
 * as nove seções autenticadas nunca foram vistas — está registrado como o
 * item 5 do handoff, e custou caro. Das quatro armadilhas de cor achadas numa
 * sessão, três só apareceram porque deu para montar harness na mão; nas telas
 * de dentro nem isso dá.
 *
 * ── Por que NÃO é um bypass no backend ───────────────────────────────────
 *
 * O caminho óbvio seria o backend aceitar um token de desenvolvimento. Seria
 * uma porta dos fundos numa aplicação que guarda a vida financeira de alguém,
 * e uma porta dos fundos existe em produção mesmo quando ninguém pretende —
 * basta uma variável de ambiente vazar de um ambiente para o outro.
 *
 * Aqui nada sai da máquina: o axios devolve os dados daqui e a rede nem é
 * tocada. O backend não sabe que isto existe, e não há o que desligar lá.
 *
 * ── Por que é impossível em produção ──────────────────────────────────────
 *
 * Duas travas, e a primeira é a que vale:
 *
 * 1. `process.env.NODE_ENV === 'development'`. O bundler substitui isso por
 *    `"production"` na build, a comparação vira `false` constante, e o ramo
 *    inteiro sai do arquivo. Não é uma checagem em tempo de execução — o
 *    código deixa de existir.
 *
 * 2. `REACT_APP_SESSAO_FALSA=1`, que precisa ser escrito à mão. Sem ele, nem
 *    em desenvolvimento a sessão liga.
 *
 * `sessaoFalsa.test.ts` procura a marca abaixo dentro do bundle de produção.
 * Se ela aparecer, o teste quebra.
 *
 * ── Onde estão os dados (05/10) ───────────────────────────────────────────
 *
 * Os dados fictícios moravam aqui. Mudaram para `demo/dados.ts`, que a
 * demonstração pública (`/demo`) também usa — e este arquivo ficou só com o
 * que é de DESENVOLVIMENTO: as duas travas, os cenários, o admin e a sessão.
 *
 * Este arquivo NÃO importa os dados, e é de propósito. Quem precisa deles os
 * busca sob demanda (`import()`), para que em produção só os baixe quem abrir
 * a demonstração. O único vínculo é o TIPO `Cenario`, que some na compilação.
 */
import type { Cenario } from '../demo/dados';

export type { Cenario };

/** Marca única. O teste procura por ela na build de produção. */
export const MARCA_DA_SESSAO_FALSA = 'SESSAO_FALSA_DE_DESENVOLVIMENTO_NAO_VAI_PARA_PRODUCAO';

export const SESSAO_FALSA_ATIVA =
  process.env.NODE_ENV === 'development' && process.env.REACT_APP_SESSAO_FALSA === '1';

/* ─── O cenário ────────────────────────────────────────────────────────── */

/**
 * Qual mundo a sessão de desenvolvimento está fingindo.
 *
 * ── Por que isto existe ───────────────────────────────────────────────────
 *
 * A primeira rodada de teste navegou o app sempre com dados, e sempre com o
 * servidor respondendo. Sobraram duas telas que ninguém tinha visto:
 *
 * 1. O app de quem acabou de se cadastrar. É a PRIMEIRA tela do produto, e
 *    toda seção tem um estado vazio desenhado que nunca foi olhado.
 * 2. O app quando a chamada falha. `utils/mensagemDeErro.ts` tem 34 linhas
 *    que nunca rodaram num navegador, e o Render free hiberna de verdade —
 *    então "o servidor caiu" é o estado normal da primeira visita do dia.
 * 3. A tela de carregamento. Com as fixtures respondendo na hora, ela some
 *    antes de o olho pegar — e é justamente a tela que a pessoa vê todo dia
 *    ao abrir o app. `lento` segura as respostas para dar tempo de olhar.
 *
 * ── Por que no localStorage e não numa variável de ambiente ───────────────
 *
 * Variável de ambiente exige reiniciar o CRA, que leva uns dez segundos por
 * troca. Trocar de cenário é justamente o que se faz muitas vezes seguidas
 * comparando telas, e dez segundos de espera fazem parar de comparar.
 *
 * A leitura é dentro de `cenario()`, não uma constante de módulo, porque uma
 * constante fixaria o valor no carregamento e a troca só valeria depois do F5.
 */

export const CHAVE_DO_CENARIO = 'dev-cenario';

/*
 * O TEMPO do cenário `lento` mudou de casa em 29/08.
 *
 * Ele morava aqui, no adapter desta sessão, e por isso só existia com
 * `REACT_APP_SESSAO_FALSA=1`. Quem rodava `npm start` normal não conseguia
 * segurar a tela de carga de jeito nenhum — e foi esse o relato, duas vezes.
 *
 * Agora mora em `dev/atrasoLocal.ts`, vale em qualquer servidor local, e
 * liga por `?lento=30` na barra de endereço em vez de console.
 */

export const cenario = (): Cenario => {
  if (!SESSAO_FALSA_ATIVA) return 'normal';
  try {
    const c = localStorage.getItem(CHAVE_DO_CENARIO);
    return c === 'vazio' || c === 'erro' || c === 'dormindo' || c === 'lento' ? c : 'normal';
  } catch {
    // localStorage bloqueado (janela anônima, política do navegador): o
    // cenário normal é o único que não depende de nada.
    return 'normal';
  }
};

/**
 * O monitor é de admin, e a sessão falsa não é admin.
 *
 * `admin: false` é a resposta honesta e continua sendo o padrão — abrir o
 * painel por acidente daria a impressão de que qualquer conta o vê.
 *
 * Mas o painel também precisava ser OLHADO, e ele é a única tela do app que
 * nenhum cenário alcançava: a seção de sugestões nasceu sem nunca ter sido
 * vista. `dev-admin=1` no localStorage liga só isso — mesma razão de o cenário
 * morar lá e não numa variável de ambiente: trocar sem reiniciar o CRA.
 */
export const CHAVE_DE_ADMIN = 'dev-admin';

export const adminFalso = (): boolean => {
  if (!SESSAO_FALSA_ATIVA) return false;
  try {
    return localStorage.getItem(CHAVE_DE_ADMIN) === '1';
  } catch {
    return false;
  }
};

/**
 * A sessão falsa DESLOGADA.
 *
 * A tela de login é a única do app que a sessão falsa nunca alcança — ela
 * existe justamente para pular o login, então o login fica do outro lado da
 * porta que ela abre.
 *
 * Isso já custou: três dos cinco textos daquela tela reprovavam em contraste
 * (1,47:1 no aviso de segurança, que é a frase que promete que o dinheiro
 * está seguro), e sobreviveram porque ninguém que trabalhava no app a via.
 *
 * `dev-deslogado=1` no localStorage devolve `null` no lugar da sessão. Mesma
 * razão de o cenário e o admin morarem lá e não numa variável de ambiente:
 * trocar sem reiniciar o CRA.
 */
export const CHAVE_DE_DESLOGADO = 'dev-deslogado';

export const deslogadoFalso = (): boolean => {
  if (!SESSAO_FALSA_ATIVA) return false;
  try {
    return localStorage.getItem(CHAVE_DE_DESLOGADO) === '1';
  } catch {
    return false;
  }
};

/* ─── O usuário ────────────────────────────────────────────────────────── */

export const usuarioFalso = {
  id: 'dev-0000-0000-0000',
  email: 'dev@fintracking.local',
  user_metadata: { full_name: 'Sessão de Teste' },
};

export const sessaoFalsa = {
  access_token: 'dev',
  token_type: 'bearer',
  expires_in: 3600,
  refresh_token: 'dev',
  user: usuarioFalso,
} as any;
